import { useCallback, useEffect, useState } from 'react';
import { daysInMonth } from '../../../util/utilsR';
import { monthArray } from '../../../constants';
import {
  IDashboardGraph,
  IDashboardGraphDataLabels,
} from '../../../ts/interface/dashboardGraph.interface';
import { useUpdateEffect } from '../../../hooks/use-update-effect';

interface UseGraphStatsProps {
  isLoadingGraphStats: boolean;
  graphStats: IDashboardGraph | null;
  timePeriod: string;
  currentMonth: number;
  currentYear: number;
  organizationId: string;
}

const getRangeArray = (start: number, end: number) => {
  return new Array(end - start + 1)
    .fill(undefined)
    .map((_, i) => i + start)
    .reverse();
};

const timeZones = require('../../../../data/timezones.json');

const useCustomGraphStats = ({
  isLoadingGraphStats,
  graphStats,
  timePeriod,
  currentMonth,
  currentYear,
  organizationId,
}: UseGraphStatsProps) => {
  const [yearArray, setYearArray] = useState<number[]>([]);
  const [systemTimezoneLabel, setSystemTimezoneLabel] = useState<string>('');
  const [rentalAvg, setRentalAvg] = useState<string>('');
  const [userTotal, setUserTotal] = useState<number>(0);
  const [userAvg, setUserAvg] = useState<string>('');
  const [durationAvg, setDurationAvg] = useState<string>('');
  const [rentalData, setRentalData] = useState<
    IDashboardGraphDataLabels | [] | undefined
  >([]);
  const [userData, setUserData] = useState<IDashboardGraphDataLabels | [] | undefined>(
    []
  );
  const [durationData, setDurationData] = useState<
    IDashboardGraphDataLabels | [] | undefined
  >([]);
  const countArrayToObj = useCallback(
    (array: { [key: string]: number }[]) => {
      const period = timePeriod === 'month' ? 'day' : 'month';
      return array.reduce((obj, item) => {
        obj[item[period]] = item.count;
        return obj;
      }, {});
    },
    [timePeriod]
  );

  const getCountData = useCallback(
    (array: { count: number; day: number }[]) => {
      if (!array?.length) {
        return {
          data: [],
          labels: [],
        };
      }

      if (timePeriod === 'month') {
        const days = daysInMonth(currentMonth + 1, yearArray[currentYear], false);
        const labels = [];
        const data = [];

        for (let i = 1; i <= days; i++) {
          labels.push(i);
          data.push(countArrayToObj(array)[i] || 0);
        }

        return { labels, data };
      }
      if (timePeriod === 'year') {
        const labels = monthArray;
        const data = [];

        for (let i = 1; i <= 12; i++) {
          data.push(countArrayToObj(array)[i] || 0);
        }

        return { labels, data };
      }
    },
    [countArrayToObj, yearArray, currentMonth, currentYear, timePeriod]
  );

  useEffect(() => {
    setYearArray(getRangeArray(2019, new Date().getFullYear()));
  }, []);

  useUpdateEffect(() => {
    if (!isLoadingGraphStats && graphStats) {
      const timezone = 'America/New_York';

      setSystemTimezoneLabel(
        timeZones.find((zone: { utc: string[] }) =>
          zone.utc.find((name: string) => name === timezone)
        )?.text
      );
      const rentalTotal = graphStats?.durations?.total_rentals || 0;
      setRentalAvg(
        timePeriod === 'month'
          ? (
              rentalTotal / daysInMonth(currentMonth, yearArray[currentYear], true)
            ).toFixed()
          : (rentalTotal / 12).toFixed()
      );

      const userTotalAux =
        graphStats?.users?.reduce((acc, curr) => acc + curr.count, 0) || 0;

      setUserTotal(userTotalAux);
      setUserAvg(
        timePeriod === 'month'
          ? (
              userTotalAux / daysInMonth(currentMonth, yearArray[currentYear], true)
            ).toFixed()
          : (userTotalAux / 12).toFixed()
      );

      const durationTotal = graphStats?.durations?.total_duration || 0;

      setDurationAvg(rentalTotal ? (durationTotal / rentalTotal).toFixed() : '0');
      if (graphStats.rentals) {
        setRentalData(getCountData(graphStats.rentals));
      }
      if (graphStats.users) {
        setUserData(getCountData(graphStats.users));
      }
      if (graphStats.durationPeriods) {
        setDurationData(getCountData(graphStats.durationPeriods));
      }
    }
  }, [
    isLoadingGraphStats,
    graphStats,
    timePeriod,
    currentMonth,
    currentYear,
    yearArray,
    getCountData,
  ]);

  return {
    yearArray,
    rentalAvg,
    userTotal,
    rentalData,
    durationAvg,
    userAvg,
    userData,
    durationData,
    systemTimezoneLabel,
  };
};

export default useCustomGraphStats;
